/**
 * A recipe's estimates: gravity, color, bitterness, final gravity and alcohol (docs/brewing-math.md).
 *
 * Each method is a published one, written from its source, and set up as BeerSmith runs it, because
 * BeerSmith is what White Oak's brewers check against: on Scott's six recipes, original gravity, color
 * and the bitterness of every addition match BeerSmith's own figures exactly ("What BeerSmith actually
 * does"). Final gravity matches on most; ABV is the usual formula until Scott's settings say which one
 * BeerSmith uses.
 *
 * These are estimates, so they are worked in floating point: an exponential has no exact decimal. What
 * a recipe stores (amounts, figures, volumes) stays exact; only what is derived from it here is not, and
 * it is shown as an estimate, never kept as a fact. A brew stores what was measured.
 */

const LITERS_PER_GALLON = 3.785411784;
const GRAMS_PER_POUND = 453.59237;

/** Gravity points a pound of sucrose gives in a gallon: BeerSmith's potential is 1 + 0.46 × yield. */
const SUCROSE_POINTS = 46;

/**
 * Tinseth as BeerSmith computes it runs 0.35% under the published constants, on every addition and at
 * every altitude Scott's recipes cover. Applied so a recipe reads as it does in BeerSmith.
 */
export const BEERSMITH_TINSETH_FACTOR = 0.99647;

export const FERMENTABLE_TYPES = ["grain", "adjunct", "sugar", "extract", "dry_extract"] as const;
export type FermentableType = (typeof FERMENTABLE_TYPES)[number];

/** Sugars and extracts give all their extract; anything mashed gives the recipe's efficiency of it. */
const FULL_EXTRACT: ReadonlySet<FermentableType> = new Set(["sugar", "extract", "dry_extract"]);

export const HOP_USES = ["mash", "first_wort", "boil", "whirlpool", "dry_hop"] as const;
export type HopUse = (typeof HOP_USES)[number];

/** What a hop is in the kettle: pellets and plugs release more than whole cones. */
export const RECIPE_HOP_FORMS = ["pellet", "plug", "leaf"] as const;
export type RecipeHopForm = (typeof RECIPE_HOP_FORMS)[number];

/** BeerSmith's defaults (Options > Bitterness), the same on Scott's machine and Tyler's. */
const FORM_FACTOR: Record<RecipeHopForm, number> = { pellet: 1.1, plug: 1.02, leaf: 1 };
const USE_FACTOR: Record<HopUse, number> = {
  mash: 0.2,
  first_wort: 1.1,
  boil: 1,
  whirlpool: 1,
  dry_hop: 0,
};

/** A recipe's own settings: no brewhouse profile yet (Scott, 2026-09-30). Volumes in liters. */
export interface RecipeSettings {
  batchLiters: number;
  boilMinutes: number;
  /** Brewhouse efficiency, as a percentage. */
  efficiencyPct: number;
  /** What the boil drives off, over the whole boil. */
  boilOffLiters: number;
  /** Left in the kettle with the trub and the chiller. */
  trubLossLiters: number;
  /** Wort shrinks as it cools, as a percentage of the post-boil volume. */
  coolingLossPct: number;
  /** The brewery's elevation: water boils cooler higher up, and hops give less. */
  altitudeFeet: number;
  /** The main rest's temperature; a cooler mash ferments further. Null when not given. */
  mashTempC: number | null;
  /**
   * How far the brewer expects it to ferment, as an apparent attenuation (Scott's Jam, 2026-10-02:
   * "expected attenuation 80% or expected final gravity"). Set, it decides the final gravity in place
   * of the yeast. A recipe gives this or `expectedFg`, never both.
   */
  expectedAttenuationPct?: number | null;
  /** The final gravity the brewer expects, which says the attenuation from the original gravity. */
  expectedFg?: number | null;
}

export interface FermentableLine {
  kind: "fermentable";
  grams: number;
  type: FermentableType;
  /** Extract potential, fine grind, dry basis, as a percentage of sucrose's. */
  yieldPct: number;
  /** Color in degrees Lovibond. */
  colorLovibond: number;
}

export interface HopLine {
  kind: "hop";
  grams: number;
  alphaPct: number;
  form: RecipeHopForm;
  use: HopUse;
  /** Boil time left when it goes in, or the steep's length in the whirlpool. */
  minutes: number;
  /** The whirlpool's temperature; ignored for other uses. */
  tempC?: number | null;
}

export interface YeastLine {
  kind: "yeast";
  /** The apparent attenuation expected of it: one figure, not the maker's range (Scott, 2026-10-02). */
  attenuationPct: number;
}

/** Spices, finings, water salts: no part of the estimates. */
export interface OtherLine {
  kind: "other";
}

export type RecipeLine = FermentableLine | HopLine | YeastLine | OtherLine;

export interface LineEstimate {
  /** A fermentable's share of the grist by weight, as a percentage. */
  gristPct?: number;
  /** The gravity points it gives the batch. */
  points?: number;
  /** A hop addition's bitterness. */
  ibu?: number;
}

export interface RecipeEstimate {
  og: number;
  /** Null when the recipe has no yeast, and no expected attenuation or FG, to say how far it ferments. */
  fg: number | null;
  /** The apparent attenuation that final gravity is: the brewer's, from their FG, or the yeast's. */
  attenuationPct: number | null;
  abv: number | null;
  ibu: number;
  /** Morey's SRM. */
  srm: number;
  /** The wort's gravity as the boil starts, which the hops see. */
  preBoilGravity: number;
  preBoilLiters: number;
  postBoilLiters: number;
  lines: LineEstimate[];
}

/** Volumes around the boil: the post-boil volume shrinks by the cooling loss to give the batch. */
export function boilVolumes(s: RecipeSettings): { preBoilLiters: number; postBoilLiters: number } {
  const postBoilLiters = (s.batchLiters + s.trubLossLiters) / (1 - s.coolingLossPct / 100);
  return { preBoilLiters: postBoilLiters + s.boilOffLiters, postBoilLiters };
}

/**
 * How much less a hop gives at altitude. Provisional: BeerSmith's factor is known at two points, 1 at sea
 * level and 0.95701 at White Oak's 765 ft, and taken as a straight line between them until Scott exports a
 * recipe at 5,000 ft (docs/brewing-math.md, question 2).
 */
export function altitudeFactor(feet: number): number {
  return 1 - ((1 - 0.95701) / 765) * Math.max(0, feet);
}

/**
 * Tinseth's utilization (Glenn Tinseth, 1995): a gravity factor 1.65 × 0.000125^(G − 1) and a time factor
 * (1 − e^(−0.04 t)) ÷ 4.15.
 */
export function tinsethUtilization(gravity: number, minutes: number): number {
  const bigness = 1.65 * 0.000125 ** (gravity - 1);
  const time = (1 - Math.exp(-0.04 * minutes)) / 4.15;
  return bigness * time;
}

/**
 * How much of the boil's isomerization happens at a whirlpool temperature, 2.39 × 10¹¹ × e^(−9773 / T)
 * with T in kelvin: BeerSmith's documented model, after Malowicki's rate constants. 1 at boiling.
 */
export function whirlpoolUtilization(tempC: number): number {
  return 2.39e11 * Math.exp(-9773 / (tempC + 273.15));
}

/** Morey's color: SRM = 1.4922 × MCU^0.6859 (Dan Morey, 1990s). */
export function moreySrm(mcu: number): number {
  return mcu <= 0 ? 0 : 1.4922 * mcu ** 0.6859;
}

/**
 * How far a yeast ferments in this recipe: its attenuation, plus 1.25% for each °F the mash is cooler
 * than 153.5°F (less for a warmer one), BeerSmith's documented defaults. A yeast's figure used to be the
 * middle of its range, as BeerSmith takes it, and is that middle for a recipe that had a range.
 */
export function apparentAttenuation(yeast: YeastLine, mashTempC: number | null): number {
  if (mashTempC === null) return yeast.attenuationPct;
  const mashF = (mashTempC * 9) / 5 + 32;
  return yeast.attenuationPct + 1.25 * (153.5 - mashF);
}

/**
 * The final gravity and the apparent attenuation it is. An expected FG is taken as it is and says the
 * attenuation; an expected attenuation says the FG; with neither, the yeast that goes furthest decides,
 * where there are several. Null with none of the three.
 */
export function finishOf(
  og: number,
  settings: Pick<RecipeSettings, "mashTempC" | "expectedAttenuationPct" | "expectedFg">,
  lines: readonly RecipeLine[],
): { fg: number | null; attenuationPct: number | null } {
  const points = og - 1;
  const { expectedFg, expectedAttenuationPct } = settings;
  if (expectedFg != null) {
    return {
      fg: expectedFg,
      attenuationPct: points > 0 ? ((og - expectedFg) / points) * 100 : null,
    };
  }
  const yeasts = lines.filter((l): l is YeastLine => l.kind === "yeast");
  const attenuationPct =
    expectedAttenuationPct ??
    (yeasts.length
      ? Math.max(...yeasts.map((y) => apparentAttenuation(y, settings.mashTempC)))
      : null);
  return {
    fg: attenuationPct === null ? null : 1 + points * (1 - attenuationPct / 100),
    attenuationPct,
  };
}

/** The usual estimate: (OG − FG) × 131.25. Provisional until BeerSmith's own method is known. */
export function abvOf(og: number, fg: number): number {
  return (og - fg) * 131.25;
}

export function estimateRecipe(
  settings: RecipeSettings,
  lines: readonly RecipeLine[],
): RecipeEstimate {
  const batchGal = settings.batchLiters / LITERS_PER_GALLON;
  const colorGal = (settings.batchLiters + settings.trubLossLiters) / LITERS_PER_GALLON;
  const grist = lines
    .filter((l): l is FermentableLine => l.kind === "fermentable")
    .reduce((n, l) => n + l.grams, 0);

  // Gravity and color first: the hops need the gravity.
  let points = 0;
  let mcu = 0;
  const estimates: LineEstimate[] = lines.map((line) => {
    if (line.kind !== "fermentable") return {};
    const pounds = line.grams / GRAMS_PER_POUND;
    const efficiency = FULL_EXTRACT.has(line.type) ? 1 : settings.efficiencyPct / 100;
    const linePoints =
      batchGal > 0 ? (pounds * SUCROSE_POINTS * (line.yieldPct / 100) * efficiency) / batchGal : 0;
    points += linePoints;
    if (colorGal > 0) mcu += (pounds * line.colorLovibond) / colorGal;
    return { points: linePoints, gristPct: grist > 0 ? (line.grams / grist) * 100 : 0 };
  });
  const og = 1 + points / 1000;

  // Bitterness: Tinseth at the pre-boil gravity, in the batch plus what stays behind with the trub.
  const { preBoilLiters, postBoilLiters } = boilVolumes(settings);
  const hopLiters = settings.batchLiters + settings.trubLossLiters;
  const preBoilGravity = preBoilLiters > 0 ? 1 + (og - 1) * (hopLiters / preBoilLiters) : og;
  let ibu = 0;
  lines.forEach((line, i) => {
    if (line.kind !== "hop") return;
    const minutes =
      line.use === "first_wort" || line.use === "mash" ? settings.boilMinutes : line.minutes;
    let utilization = tinsethUtilization(preBoilGravity, minutes) * BEERSMITH_TINSETH_FACTOR;
    if (line.use === "whirlpool") {
      utilization *= whirlpoolUtilization(line.tempC ?? 100);
    } else {
      utilization *= altitudeFactor(settings.altitudeFeet);
    }
    utilization *= FORM_FACTOR[line.form] * USE_FACTOR[line.use];
    const lineIbu =
      hopLiters > 0 ? (utilization * (line.alphaPct / 100) * line.grams * 1000) / hopLiters : 0;
    ibu += lineIbu;
    estimates[i] = { ibu: lineIbu };
  });

  // How far it ferments: as the brewer expects, where they say, else the yeast that goes furthest.
  const { fg, attenuationPct } = finishOf(og, settings, lines);

  return {
    og,
    fg,
    attenuationPct,
    abv: fg === null ? null : abvOf(og, fg),
    ibu,
    srm: moreySrm(mcu),
    preBoilGravity,
    preBoilLiters,
    postBoilLiters,
    lines: estimates,
  };
}
